import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import CloseIcon from 'mdi-react/CloseIcon';
import Select from 'react-select';
import moment from 'moment';
import QuarterHourTimeSelect from '../../common/QuarterHourTimeSelect';
import { useRecoilValue } from 'recoil';
import { useSelector } from 'react-redux';
import { useGetFacilityServices } from '../../App/customize/hooks/useGetFacilityServices';
import { useGetAppointmentTypes } from '../../App/customize/hooks/useGetAppointmentTypes';
import { useAppointmentLabels } from '../../App/customize/hooks/useAppointmentLabels';
import PetSearchSelect from '../../common/PetSearchSelect';
import AppointmentTypesSelect from '../../common/AppointmentTypesSelect';
import DatePickerComponent from '../../Web/Common/DatePickerComponent';
import { DEFAULT_TIMESTAMP_FORMAT } from '../../../constants/constants';
import { formatPhone } from '../../../libs/commonHelper';
import { handleRequest } from '../../../redux/services/APIService';
import { useFocusedUtilityEscape } from '../../../hooks/useFocusedUtilityEscape';
import { useNewAppointmentUtility } from './NewAppointmentUtility.hooks';
import { newAppointmentUtilityStateAtom } from './NewAppointmentUtility.state';
import {
  formatStaffDoctorOptionLabel,
  mapStaffDoctorsFromApi,
  mergeStaffDoctorSelectStyles,
} from '../../../utils/staffDoctorSelect';

const SIDEBAR_GAP = 16;
const VIEWPORT_MARGIN = 12;
const PREFERRED_TOP = 260;
const SIDEBAR_TRANSITION_FALLBACK_MS = 300;
const DRAG_THRESHOLD_PX = 5;
const SUCCESS_NOTICE_MS = 3750;
const DEFAULT_PET_AVATAR = `${process.env.PUBLIC_URL}/img/default-animal-black.png`;
const DAYCARE_TYPE_TITLE = 'Daycare';
const DUPLICATE_SAME_DAY_CODE = 'DUPLICATE_APPOINTMENT_SAME_DAY';

const DOCTOR_SELECT_STYLES = {
  control: (base, state) => ({
    ...base,
    minHeight: 32,
    height: 32,
    fontSize: 13,
    borderColor: state.isFocused ? '#00b0ec' : '#e0e2e0',
    boxShadow: 'none',
    borderRadius: 4,
  }),
  valueContainer: (base) => ({
    ...base,
    padding: '0 8px',
    height: 30,
  }),
  input: (base) => ({
    ...base,
    margin: 0,
    padding: 0,
  }),
  indicatorsContainer: (base) => ({
    ...base,
    height: 30,
  }),
  dropdownIndicator: (base) => ({
    ...base,
    padding: '0 6px',
  }),
  clearIndicator: (base) => ({
    ...base,
    padding: '0 4px',
  }),
  placeholder: (base) => ({
    ...base,
    color: '#818383',
    fontSize: 13,
  }),
  singleValue: (base) => ({
    ...base,
    fontSize: 13,
    color: '#15153e',
  }),
  menuPortal: (base) => ({
    ...base,
    zIndex: 2000,
  }),
};

function getLayoutMetrics() {
  const sidebar = document.querySelector('.sidebar');
  const sidebarRect = sidebar?.getBoundingClientRect();
  const sidebarRight = sidebarRect?.right ?? 240;
  return {
    sidebarRight,
    preferredTop: PREFERRED_TOP,
    preferredLeft: Math.round(sidebarRight + SIDEBAR_GAP),
  };
}

function resolvePanelPosition(panelRect) {
  const { sidebarRight, preferredTop, preferredLeft } = getLayoutMetrics();
  const width = panelRect?.width || 0;
  const height = panelRect?.height || 0;
  const viewportWidth = window.innerWidth;
  const viewportHeight = window.innerHeight;

  const maxLeft = Math.max(
    VIEWPORT_MARGIN,
    viewportWidth - width - VIEWPORT_MARGIN,
  );
  const maxTop = Math.max(
    VIEWPORT_MARGIN,
    viewportHeight - height - VIEWPORT_MARGIN,
  );

  let left = Math.min(Math.max(preferredLeft, VIEWPORT_MARGIN), maxLeft);
  const top = Math.min(Math.max(preferredTop, VIEWPORT_MARGIN), maxTop);

  const sidebarSafeLeft = sidebarRight + SIDEBAR_GAP;
  const fitsBesideSidebar =
    width > 0 && sidebarSafeLeft + width + VIEWPORT_MARGIN <= viewportWidth;

  if (fitsBesideSidebar) {
    left = Math.max(left, sidebarSafeLeft);
    left = Math.min(left, maxLeft);
  }

  return {
    top: Math.round(top),
    left: Math.round(left),
  };
}

function clampToViewport(top, left, panelRect) {
  const width = panelRect?.width || 0;
  const height = panelRect?.height || 0;
  const maxLeft = Math.max(
    VIEWPORT_MARGIN,
    window.innerWidth - width - VIEWPORT_MARGIN,
  );
  const maxTop = Math.max(
    VIEWPORT_MARGIN,
    window.innerHeight - height - VIEWPORT_MARGIN,
  );
  return {
    top: Math.round(Math.min(Math.max(top, VIEWPORT_MARGIN), maxTop)),
    left: Math.round(Math.min(Math.max(left, VIEWPORT_MARGIN), maxLeft)),
  };
}

function parentDisplayName(parent) {
  if (!parent) {
    return '';
  }
  if (parent.name) {
    return parent.name;
  }
  return `${parent.firstName || ''} ${parent.lastName || ''}`.trim();
}

function parentPhoneDisplay(parent) {
  const digits = String(parent?.phone || '').replace(/\D/g, '');
  if (!digits) {
    return '';
  }
  return formatPhone(digits, '(###) ###-####');
}

function getRoundedMomentTime() {
  const date = moment();

  if (date.minute() <= 10 && date.minute() > 0) {
    date.minute(0);
  } else {
    const roundedUp = Math.ceil(date.minute() / 15) * 15;
    date.minute(roundedUp);
  }
  date.second(0);
  return date;
}

function getDefaultAppointmentTime() {
  return moment().hours(13).minutes(0).seconds(0).milliseconds(0);
}

function getDefaultAppointmentDate(utcOffset = 0) {
  return moment()
    .utc()
    .add(utcOffset || 0, 'minutes')
    .startOf('date')
    .add(1, 'day')
    .format(DEFAULT_TIMESTAMP_FORMAT);
}

function isDuplicateSameDayError(error) {
  const payload = error?.response?.data;
  const text = [
    payload?.errorMessage,
    payload?.error,
    payload?.data,
    payload?.message,
    error?.message,
  ]
    .filter(Boolean)
    .join(' ');
  return text.includes(DUPLICATE_SAME_DAY_CODE);
}

function duplicateAppointmentSameDayMessage(petName) {
  const name = String(petName || '').trim() || 'This pet';
  return `${name} already has an appointment at your facility on this day.`;
}

function getCreateErrorMessage(error) {
  return (
    error?.response?.data?.errorMessage ||
    error?.response?.data?.message ||
    error?.response?.data?.data?.resultMessage ||
    error?.data?.message ||
    error?.message ||
    'Could not create appointment'
  );
}

function isBoardingDateValid(appointmentDate) {
  if (!appointmentDate) {
    return false;
  }
  const selectedDay = moment(appointmentDate, DEFAULT_TIMESTAMP_FORMAT).startOf(
    'day',
  );
  if (!selectedDay.isValid()) {
    return false;
  }
  return !selectedDay.isBefore(moment().startOf('day'));
}

function isVetScheduleValid(appointmentDate, appointmentTime) {
  if (!appointmentDate || !appointmentTime) {
    return false;
  }
  const dt = moment(appointmentDate, DEFAULT_TIMESTAMP_FORMAT).format(
    DEFAULT_TIMESTAMP_FORMAT,
  );
  const dateTime = moment(`${dt} ${appointmentTime.format('hh:mm A')}`);
  if (!dateTime.isValid()) {
    return false;
  }
  const difference = dateTime.diff(moment.now());
  const differenceInMinutes = moment.duration(difference).asMinutes();
  if (
    (differenceInMinutes < -11 && dateTime.minute() > 11) ||
    (difference < 0 && dateTime.minute() > 11) ||
    (differenceInMinutes < -11 && difference < 0)
  ) {
    return false;
  }
  return true;
}

function isDaycareEndDateValid(appointmentDate, endDate) {
  if (!appointmentDate || !endDate) {
    return false;
  }
  const start = moment(appointmentDate, DEFAULT_TIMESTAMP_FORMAT).startOf('day');
  const end = moment(endDate, DEFAULT_TIMESTAMP_FORMAT).startOf('day');
  if (!start.isValid() || !end.isValid()) {
    return false;
  }
  return !end.isBefore(start);
}

function daycareDates(appointmentDate, endDate) {
  const start = moment(appointmentDate, DEFAULT_TIMESTAMP_FORMAT).startOf('day');
  const end = moment(endDate, DEFAULT_TIMESTAMP_FORMAT).startOf('day');
  const dates = [];
  const current = start.clone();
  while (current.isSameOrBefore(end, 'day')) {
    dates.push(current.clone());
    current.add(1, 'day');
  }
  return dates;
}

function focusSearchField() {
  requestAnimationFrame(() => {
    const el = document.querySelector(
      '.new-appointment-utility input[name="addToBoardSearch"]',
    );
    if (el && typeof el.focus === 'function') {
      el.focus();
    }
  });
}

export default function NewAppointmentUtility() {
  const { open } = useRecoilValue(newAppointmentUtilityStateAtom);
  const { closeNewAppointmentUtility } = useNewAppointmentUtility();
  const { data: services } = useGetFacilityServices();
  const { data: appointmentTypes } = useGetAppointmentTypes({
    filter: { checked: 1 },
  });
  const labels = useAppointmentLabels();
  const isBoardingOnly = !!services?.isBoardingOnly;

  const facilityUtcOffset =
  useSelector((state) => state.user?.profile?.facility?.utcOffset) || 0;

  const [selectedPet, setSelectedPet] = useState(null);
  const [appointmentDate, setAppointmentDate] = useState(null);
  const [appointmentTime, setAppointmentTime] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [doctor, setDoctor] = useState(null);
  const [appointmentType, setAppointmentType] = useState('');
  const [staffDoctorList, setStaffDoctorList] = useState([]);
  const [doctorMenuOpen, setDoctorMenuOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [statusNotice, setStatusNotice] = useState('');
  const [position, setPosition] = useState({
    top: PREFERRED_TOP,
    left: 256,
  });
  const panelRef = useRef(null);
  const sidebarTransitionCleanupRef = useRef(null);
  const userMovedRef = useRef(false);
  const positionRef = useRef({ top: PREFERRED_TOP, left: 256 });
  const dragRef = useRef(null);
  const statusNoticeTimerRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  positionRef.current = position;

  const selectedType = (appointmentTypes || []).find(
    (type) => String(type._id) === String(appointmentType),
  );
  const isDaycare = selectedType?.title === DAYCARE_TYPE_TITLE;
  const hasValidSchedule = isBoardingOnly
    ? isBoardingDateValid(appointmentDate)
    : isVetScheduleValid(appointmentDate, appointmentTime);
  const hasValidEndDate = !isDaycare || isDaycareEndDateValid(appointmentDate, endDate);
  const canCreate =
    Boolean(selectedPet?.pet) &&
    Boolean(appointmentType) &&
    hasValidSchedule &&
    hasValidEndDate &&
    !submitting;

  const clearStatusNoticeTimer = useCallback(() => {
    if (statusNoticeTimerRef.current) {
      clearTimeout(statusNoticeTimerRef.current);
      statusNoticeTimerRef.current = null;
    }
  }, []);

  const clearSidebarTransitionWatch = useCallback(() => {
    if (sidebarTransitionCleanupRef.current) {
      sidebarTransitionCleanupRef.current();
      sidebarTransitionCleanupRef.current = null;
    }
  }, []);

  const resetForm = useCallback(() => {
    setSelectedPet(null);
    setAppointmentDate(null);
    setAppointmentTime(null);
    setEndDate(null);
    setDoctor(null);
    setAppointmentType('');
    setDoctorMenuOpen(false);
    setSubmitting(false);
  }, []);

  const applyPosition = useCallback(() => {
    const panelEl = panelRef.current;
    const rect = panelEl ? panelEl.getBoundingClientRect() : null;

    if (userMovedRef.current) {
      const current = positionRef.current;
      const next = rect
        ? clampToViewport(current.top, current.left, rect)
        : current;
      setPosition((prev) =>
        prev.top === next.top && prev.left === next.left ? prev : next,
      );
      return;
    }

    const next = rect
      ? resolvePanelPosition(rect)
      : (() => {
          const { preferredTop, preferredLeft } = getLayoutMetrics();
          return { top: preferredTop, left: preferredLeft };
        })();
    setPosition((prev) =>
      prev.top === next.top && prev.left === next.left ? prev : next,
    );
  }, []);

  const updatePosition = useCallback(() => {
    applyPosition();
    requestAnimationFrame(() => {
      requestAnimationFrame(applyPosition);
    });
  }, [applyPosition]);

  const watchSidebarWidthTransition = useCallback(() => {
    clearSidebarTransitionWatch();
    const sidebar = document.querySelector('.sidebar');
    if (!sidebar) {
      updatePosition();
      return;
    }

    const onTransitionEnd = (event) => {
      if (event.target !== sidebar || event.propertyName !== 'width') {
        return;
      }
      clearSidebarTransitionWatch();
      updatePosition();
    };

    const fallbackTimer = setTimeout(() => {
      clearSidebarTransitionWatch();
      updatePosition();
    }, SIDEBAR_TRANSITION_FALLBACK_MS);

    sidebar.addEventListener('transitionend', onTransitionEnd);
    sidebarTransitionCleanupRef.current = () => {
      clearTimeout(fallbackTimer);
      sidebar.removeEventListener('transitionend', onTransitionEnd);
    };
  }, [clearSidebarTransitionWatch, updatePosition]);

  const closeUtility = useCallback(() => {
    userMovedRef.current = false;
    dragRef.current = null;
    setIsDragging(false);
    clearStatusNoticeTimer();
    setStatusNotice('');
    resetForm();
    closeNewAppointmentUtility();
  }, [clearStatusNoticeTimer, closeNewAppointmentUtility, resetForm]);

  const handleSelectPet = useCallback(
    ({ pet, parent }) => {
      setSelectedPet({ pet, parent });
      setAppointmentDate(getDefaultAppointmentDate(facilityUtcOffset));
      setAppointmentTime(isBoardingOnly ? null : getDefaultAppointmentTime());
      setEndDate(null);
      setDoctor(null);
      setAppointmentType('');
      setStatusNotice('');
      requestAnimationFrame(updatePosition);
    },
    [facilityUtcOffset, isBoardingOnly, updatePosition],
  );

  const handleClearSelectedPet = useCallback(() => {
    setSelectedPet(null);
    setAppointmentDate(null);
    setAppointmentTime(null);
    setEndDate(null);
    setDoctor(null);
    setAppointmentType('');
    setDoctorMenuOpen(false);
    setStatusNotice('');
    requestAnimationFrame(() => {
      updatePosition();
      focusSearchField();
    });
  }, [updatePosition]);

  const handleCreate = useCallback(async () => {
    const pet = selectedPet?.pet;
    const parent = selectedPet?.parent;
    if (!pet || !parent || !canCreate) {
      return;
    }

    clearStatusNoticeTimer();
    setStatusNotice('');
    setSubmitting(true);

    const timeMoment = isBoardingOnly
      ? getRoundedMomentTime()
      : appointmentTime;
    const basePayload = {
      parentId: parent._id,
      pet: pet._id,
      time: timeMoment.format('hh:mm A'),
      type: appointmentType,
      typeOther: '',
      doctor: doctor || null,
      referringDoctor: null,
      shareUpdates: false,
    };

    try {
      if (isDaycare) {
        const dates = daycareDates(appointmentDate, endDate);
        for (const date of dates) {
          await handleRequest('post', 'appointment', true, {
            ...basePayload,
            appointmentDate: date.format(DEFAULT_TIMESTAMP_FORMAT),
          });
        }
      } else {
        await handleRequest('post', 'appointment', true, {
          ...basePayload,
          appointmentDate: moment(
            appointmentDate,
            DEFAULT_TIMESTAMP_FORMAT,
          ).format(DEFAULT_TIMESTAMP_FORMAT),
        });
      }

      setStatusNotice(
        labels?.appointmentSuccessfullyCreated || 'Appointment Successfully Created',
      );
      requestAnimationFrame(updatePosition);
      statusNoticeTimerRef.current = setTimeout(() => {
        statusNoticeTimerRef.current = null;
        resetForm();
        setStatusNotice('');
        requestAnimationFrame(() => {
          updatePosition();
          focusSearchField();
        });
      }, SUCCESS_NOTICE_MS);
    } catch (error) {
      setSubmitting(false);
      if (isDuplicateSameDayError(error)) {
        resetForm();
        setStatusNotice(duplicateAppointmentSameDayMessage(pet.name));
        requestAnimationFrame(() => {
          updatePosition();
          focusSearchField();
        });
        statusNoticeTimerRef.current = setTimeout(() => {
          statusNoticeTimerRef.current = null;
          setStatusNotice('');
          requestAnimationFrame(updatePosition);
        }, SUCCESS_NOTICE_MS);
        return;
      }
      setStatusNotice(getCreateErrorMessage(error));
      requestAnimationFrame(updatePosition);
    }
  }, [
    appointmentDate,
    appointmentTime,
    appointmentType,
    canCreate,
    clearStatusNoticeTimer,
    doctor,
    endDate,
    isBoardingOnly,
    isDaycare,
    labels,
    resetForm,
    selectedPet,
    updatePosition,
  ]);

  const onTitlePointerDown = useCallback((e) => {
    if (e.button != null && e.button !== 0) {
      return;
    }
    e.preventDefault();
    setDoctorMenuOpen(false);
    const target = e.currentTarget;
    if (typeof target.setPointerCapture === 'function') {
      target.setPointerCapture(e.pointerId);
    }
    dragRef.current = {
      pointerId: e.pointerId,
      startX: e.clientX,
      startY: e.clientY,
      originTop: positionRef.current.top,
      originLeft: positionRef.current.left,
      moved: false,
    };
  }, []);

  const onTitlePointerMove = useCallback((e) => {
    const drag = dragRef.current;
    if (!drag || drag.pointerId !== e.pointerId) {
      return;
    }
    const dx = e.clientX - drag.startX;
    const dy = e.clientY - drag.startY;
    if (!drag.moved) {
      if (Math.hypot(dx, dy) < DRAG_THRESHOLD_PX) {
        return;
      }
      drag.moved = true;
      userMovedRef.current = true;
      setIsDragging(true);
    }
    const panelEl = panelRef.current;
    const rect = panelEl
      ? panelEl.getBoundingClientRect()
      : { width: 0, height: 0 };
    const next = clampToViewport(
      drag.originTop + dy,
      drag.originLeft + dx,
      rect,
    );
    setPosition(next);
  }, []);

  const endTitlePointerDrag = useCallback((e) => {
    const drag = dragRef.current;
    if (!drag || drag.pointerId !== e.pointerId) {
      return;
    }
    const target = e.currentTarget;
    if (
      typeof target.hasPointerCapture === 'function' &&
      target.hasPointerCapture(e.pointerId) &&
      typeof target.releasePointerCapture === 'function'
    ) {
      target.releasePointerCapture(e.pointerId);
    }
    dragRef.current = null;
    setIsDragging(false);
  }, []);

  useEffect(() => {
    if (!open) {
      return undefined;
    }
    userMovedRef.current = false;
    dragRef.current = null;
    setIsDragging(false);
    clearStatusNoticeTimer();
    setStatusNotice('');
    resetForm();
    updatePosition();
    focusSearchField();

    const onResize = () => updatePosition();
    window.addEventListener('resize', onResize);

    const sidebar = document.querySelector('.sidebar');
    const observer = sidebar
      ? new MutationObserver(() => {
          updatePosition();
          watchSidebarWidthTransition();
        })
      : null;
    if (sidebar && observer) {
      observer.observe(sidebar, {
        attributes: true,
        attributeFilter: ['class'],
      });
    }

    return () => {
      window.removeEventListener('resize', onResize);
      if (observer) {
        observer.disconnect();
      }
      clearSidebarTransitionWatch();
    };
  }, [
    clearSidebarTransitionWatch,
    clearStatusNoticeTimer,
    open,
    resetForm,
    updatePosition,
    watchSidebarWidthTransition,
  ]);

  useEffect(() => {
    if (!open || isBoardingOnly) {
      return undefined;
    }
    let cancelled = false;
    handleRequest('get', 'staff-doctors/by-facility', true)
      .then((results) => {
        if (!cancelled) {
          setStaffDoctorList(mapStaffDoctorsFromApi(results?.data));
        }
      })
      .catch(() => {
        if (!cancelled) {
          setStaffDoctorList([]);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [open, isBoardingOnly]);

  useFocusedUtilityEscape({
    enabled: open,
    panelRef,
    onEscape: closeUtility,
  });

  useEffect(
    () => () => {
      clearSidebarTransitionWatch();
      clearStatusNoticeTimer();
    },
    [clearSidebarTransitionWatch, clearStatusNoticeTimer],
  );

  if (!open) {
    return null;
  }

  const pet = selectedPet?.pet;
  const parent = selectedPet?.parent;
  const parentPhone = parentPhoneDisplay(parent);
  const title = labels?.newAppointment ?? 'New Appointment';
  const typeLabel = labels?.appointmentType ?? 'Appointment Type';
  const dateLabel = labels?.appointmentDate ?? 'Appointment Date';
  const showDoctorAndType =
  Boolean(pet) &&
  !isBoardingOnly &&
  Boolean(appointmentDate) &&
  Boolean(appointmentTime);
  const showBoardingType = Boolean(pet) && isBoardingOnly;
  const showEndDate = showBoardingType && isDaycare;

  return createPortal(
    <div
      ref={panelRef}
      className="new-appointment-utility"
      style={{ top: position.top, left: position.left }}
    >
      <div className="new-appointment-utility__panel">
        <div
          className={
            'new-appointment-utility__title' +
            (isDragging ? ' new-appointment-utility__title--dragging' : '')
          }
          onPointerDown={onTitlePointerDown}
          onPointerMove={onTitlePointerMove}
          onPointerUp={endTitlePointerDrag}
          onPointerCancel={endTitlePointerDrag}
        >
          {title}
        </div>
        <form
          className="new-appointment-utility__body"
          autoComplete="off"
          noValidate
          onSubmit={(e) => e.preventDefault()}
        >
          {pet ? (
            <div className="new-appointment-utility__selected">
              <img
                className="new-appointment-utility__selected-avatar"
                src={pet.image || DEFAULT_PET_AVATAR}
                alt=""
              />
              <span className="new-appointment-utility__selected-names">
                <span className="new-appointment-utility__selected-line">
                  <span className="new-appointment-utility__selected-pet-name">
                    {pet.name}
                  </span>
                  <span className="new-appointment-utility__selected-separator">
                    {' \u2014 '}
                  </span>
                  <span className="new-appointment-utility__selected-parent-name">
                    {parentDisplayName(parent)}
                  </span>
                </span>
                {parentPhone ? (
                  <span className="new-appointment-utility__selected-phone">
                    {parentPhone}
                  </span>
                ) : null}
              </span>
              <button
                type="button"
                className="new-appointment-utility__selected-clear"
                aria-label="Clear selected pet"
                onClick={handleClearSelectedPet}
              >
                <CloseIcon size={16} />
              </button>
            </div>
          ) : (
            <PetSearchSelect autoFocus onSelectPet={handleSelectPet} />
          )}
          {pet ? (
            <div className="new-appointment-utility__fields">
              <label className="new-appointment-utility__field">
                <span className="new-appointment-utility__label">
                  {dateLabel}{' '}
                  <span className="new-appointment-utility__required">*</span>
                </span>
<DatePickerComponent
  date={appointmentDate}
  handleChange={(value) => {
    setAppointmentDate(value || null);
    if (isDaycare) {
      setEndDate((prev) => prev || value || null);
    }
    setStatusNotice('');
    requestAnimationFrame(updatePosition);
  }}
  minDate
  portalMenu
  compact
/>
              </label>
              {!isBoardingOnly ? (
                <label className="new-appointment-utility__field">
                  <span className="new-appointment-utility__label">
                    Time{' '}
                    <span className="new-appointment-utility__required">*</span>
                  </span>
<QuarterHourTimeSelect
  value={appointmentTime}
  onChange={(value) => {
    setAppointmentTime(value || null);
    setStatusNotice('');
    requestAnimationFrame(updatePosition);
  }}
  placeholder="HH:MM AM"
  name="time"
/>
                </label>
              ) : (
                <label className="new-appointment-utility__field">
                  <span className="new-appointment-utility__label">
                    {typeLabel}{' '}
                    <span className="new-appointment-utility__required">*</span>
                  </span>
                  <AppointmentTypesSelect
                    value={appointmentType}
                    handleChange={(e) => {
                      const nextType = e.target.value;
                      const nextSelected = (appointmentTypes || []).find(
                        (type) => String(type._id) === String(nextType),
                      );
                      const nextIsDaycare =
                        nextSelected?.title === DAYCARE_TYPE_TITLE;
                      setAppointmentType(nextType);
                      setEndDate(
                        nextIsDaycare ? appointmentDate || null : null,
                      );
                      setStatusNotice('');
                      requestAnimationFrame(updatePosition);
                    }}
                  />
                </label>
              )}
            </div>
          ) : null}
          {showEndDate ? (
            <div className="new-appointment-utility__fields new-appointment-utility__fields--half">
              <label className="new-appointment-utility__field">
                <span className="new-appointment-utility__label">
                  End Date{' '}
                  <span className="new-appointment-utility__required">*</span>
                </span>
<DatePickerComponent
  date={endDate}
  handleChange={(value) => {
    setEndDate(value || null);
    setStatusNotice('');
    requestAnimationFrame(updatePosition);
  }}
  minDate={appointmentDate || true}
  portalMenu
  compact
/>
              </label>
            </div>
          ) : null}
          {showDoctorAndType ? (
            <div className="new-appointment-utility__fields">
              <label className="new-appointment-utility__field">
                <span className="new-appointment-utility__label">Doctor</span>
<Select
  classNamePrefix="nau-doctor-select"
  value={
    staffDoctorList.find((option) => option.value === doctor) ||
    null
  }
  onChange={(option) => {
    setDoctor(option ? option.value : null);
    requestAnimationFrame(updatePosition);
  }}
  options={staffDoctorList}
  formatOptionLabel={formatStaffDoctorOptionLabel}
  placeholder="Select Doctor"
  isClearable
  name="doctor"
  menuIsOpen={doctorMenuOpen}
  onMenuOpen={() => setDoctorMenuOpen(true)}
  onMenuClose={() => setDoctorMenuOpen(false)}
  onKeyDown={(e) => {
    if (
      (e.key === 'Escape' || e.keyCode === 27) &&
      doctorMenuOpen
    ) {
      e.preventDefault();
      setDoctorMenuOpen(false);
    }
  }}
  menuPortalTarget={document.body}
  menuPosition="fixed"
  menuShouldScrollIntoView={false}
  styles={mergeStaffDoctorSelectStyles(DOCTOR_SELECT_STYLES)}
/>
              </label>
              <label className="new-appointment-utility__field">
                <span className="new-appointment-utility__label">
                  {typeLabel}{' '}
                  <span className="new-appointment-utility__required">*</span>
                </span>
                <AppointmentTypesSelect
                  value={appointmentType}
                  handleChange={(e) => {
                    setAppointmentType(e.target.value);
                    setStatusNotice('');
                    requestAnimationFrame(updatePosition);
                  }}
                />
              </label>
            </div>
          ) : null}
          <div className="new-appointment-utility__actions">
            {canCreate ? (
              <button
                type="button"
                className="new-appointment-utility__create"
                disabled={submitting}
                onClick={handleCreate}
              >
                Create
              </button>
            ) : null}
            <button
              type="button"
              className="new-appointment-utility__cancel"
              onClick={closeUtility}
            >
              Cancel
            </button>
          </div>
          {statusNotice ? (
            <p className="new-appointment-utility__status-hint">
              {statusNotice}
            </p>
          ) : null}
        </form>
      </div>
    </div>,
    document.body,
  );
}
